import { type JSX, useEffect, useId, useState } from 'react';

import {
  abrechnen,
  abrechnungen as holeAbrechnungen,
  abrechnungsdatei,
  ABRECHNUNGSSTATUSNAME,
  arten as holeArten,
  bezahlt,
  erfassen,
  gutscheine as holeGutscheine,
  GUTSCHEINSTATUSNAME,
  kostentraeger as holeKostentraeger,
  kostentraegerSetzen,
  offene as holeOffene,
  stornieren,
  passendeVormerkungen as holePassendeVormerkungen,
  verwerfen,
  vormerken,
  vormerkungAendern,
  vormerkungAufloesen,
  vormerkungen as holeVormerkungen,
  wertAendern,
  wertAnlegen,
  werte as holeWerte,
  wertLoeschen,
  type Abrechnungszeile,
  type BgAbrechnungskopf,
  type BgAbrechnungsstatus,
  type BgGutschein,
  type BgGutscheinstatus,
  type Gutscheinvormerkung,
  type Gutscheinwert,
  type GutscheinwertEingabe,
  type Kostentraeger,
  type Kostentraegerart,
} from '../api/bgzuschuss';
import { ApiFehler } from '../api/fehler';
import { alsEuro } from '../betrag';
import { alsTag, heute } from '../datum';
import { Feldzeile } from '../komponenten/Akte';
import { ladeUndReiche } from '../komponenten/datei';
import { Dialog } from '../komponenten/Dialog';
import { Liste } from '../komponenten/Liste';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { alsBetragstext } from '../komponenten/Stornoangaben';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';
import { markenklasse, type Markenart } from '../marken';

/** Cent als ganze Zahl: Betragstexte summieren, ohne mit Gleitkomma zu runden. */
function alsCent(text: string): number {
  const [euro = '0', cent = ''] = text.split('.');
  return Number(euro) * 100 + Number(cent.padEnd(2, '0').slice(0, 2));
}

function centAlsText(cent: number): string {
  return `${Math.floor(cent / 100)}.${String(cent % 100).padStart(2, '0')}`;
}

function summeDer(zeilen: readonly { readonly betrag: string }[]): string {
  return centAlsText(zeilen.reduce((s, z) => s + alsCent(z.betrag), 0));
}

function alsFehler(ursache: unknown): ApiFehler | Error {
  return ursache instanceof ApiFehler || ursache instanceof Error ? ursache : new Error(String(ursache));
}

function FehlerAnzeige({ fehler }: { readonly fehler: ApiFehler | Error }): JSX.Element {
  return fehler instanceof ApiFehler ? (
    <Meldung fehler={fehler} />
  ) : (
    <p className="meldung meldung--fehler" role="alert">
      {fehler.message}
    </p>
  );
}

const GUTSCHEINMARKE: Readonly<Record<BgGutscheinstatus, Markenart>> = {
  erfasst: 'laufend',
  offen: 'aufmerksamkeit',
  abgerechnet: 'laufend',
  bezahlt: 'abschluss',
  verworfen: 'stillgelegt',
};

const ABRECHNUNGSMARKE: Readonly<Record<BgAbrechnungsstatus, Markenart>> = {
  abgerechnet: 'aufmerksamkeit',
  bezahlt: 'abschluss',
  storniert: 'stillgelegt',
};

type Artengruppe = {
  readonly art: string;
  readonly bezeichnung: string;
  readonly traeger: readonly Kostentraeger[];
};

/**
 * E-74 (CRM-163): die Kostenträger nach ihrer Art gruppiert. Der Server liefert
 * sie schon nach Reihenfolge der Art und Namen; die Gruppen behalten diese
 * Folge, statt sie hier ein zweites Mal festzulegen.
 */
function nachArt(liste: readonly Kostentraeger[]): readonly Artengruppe[] {
  const gruppen = new Map<string, { art: string; bezeichnung: string; traeger: Kostentraeger[] }>();
  for (const t of liste) {
    const gruppe = gruppen.get(t.art) ?? { art: t.art, bezeichnung: t.artBezeichnung, traeger: [] };
    gruppe.traeger.push(t);
    gruppen.set(t.art, gruppe);
  }
  return [...gruppen.values()];
}

/** Die Auswahl der Kostenträger, je Art eine `<optgroup>` (E-74, CRM-163). */
function Traegeroptionen({
  traeger,
  mitNummer = false,
}: {
  readonly traeger: readonly Kostentraeger[];
  readonly mitNummer?: boolean;
}): JSX.Element {
  return (
    <>
      {nachArt(traeger).map((g) => (
        <optgroup key={g.art} label={g.bezeichnung}>
          {g.traeger.map((t) => (
            <option key={t.id} value={t.id}>
              {mitNummer ? `${t.name} (${t.kundennummer})` : t.name}
            </option>
          ))}
        </optgroup>
      ))}
    </>
  );
}

/** "120.00" → "120,00" für ein Eingabefeld; formatiert wird nur die Anzeige. */
function alsEingabebetrag(betrag: string): string {
  return betrag.replace('.', ',');
}

/** Ein Betrag aus dem Feld: mit Punkt, größer null — sonst null. */
function gueltigerBetrag(eingabe: string): string | null {
  const text = alsBetragstext(eingabe);
  return text === null || alsCent(text) === 0 ? null : text;
}

const BETRAGSHINWEIS = 'Bitte einen Betrag in Euro angeben, etwa 120,00.';

type Frage =
  | { readonly art: 'abrechnen' }
  | { readonly art: 'bezahlt'; readonly abrechnung: BgAbrechnungskopf }
  | { readonly art: 'storno'; readonly abrechnung: BgAbrechnungskopf };

/**
 * ============================================================================
 * DIE SAMMELABRECHNUNG AN DEN KOSTENTRÄGER (PRD-38, CRM-157, CRM-163)
 * ============================================================================
 *
 * Kostenträger und Zeitraum frei wählbar; ohne Zeitraum alle offenen
 * Gutscheine. Die Auswahl gruppiert nach der Art des Kostenträgers (E-74) —
 * eine Berufsgenossenschaft, ein anderes Zentrum, ein Dienstleister. Die
 * Abrechnung ist ein Beleg mit Nummer und ohne erneute Umsatzsteuer (E-70) —
 * sie fragt deshalb nach (Tabelle 29), ebenso der Storno und der
 * Zahlungseingang.
 */
export function BgAbrechnungsmaske(): JSX.Element {
  const [traeger, setzeTraeger] = useState<readonly Kostentraeger[] | null>(null);
  const [traegerId, setzeTraegerId] = useState('');
  const [von, setzeVon] = useState('');
  const [bis, setzeBis] = useState('');
  const [offen, setzeOffen] = useState<readonly Abrechnungszeile[]>([]);
  const [belege, setzeBelege] = useState<readonly BgAbrechnungskopf[]>([]);
  const [fehler, setzeFehler] = useState<ApiFehler | Error | null>(null);
  const [rueckmeldung, setzeRueckmeldung] = useState('');
  const [frage, setzeFrage] = useState<Frage | null>(null);
  const [stand, setzeStand] = useState(0);

  useEffect(() => {
    holeKostentraeger()
      .then((liste) => {
        setzeTraeger(liste);
        setzeTraegerId((bisher) => (bisher === '' ? (liste[0]?.id ?? '') : bisher));
      })
      .catch((u: unknown) => setzeFehler(alsFehler(u)));
  }, []);

  const zeitraumFalsch = von !== '' && bis !== '' && von > bis;

  useEffect(() => {
    if (traegerId === '' || zeitraumFalsch) return;
    let aktiv = true;
    Promise.all([
      holeOffene(traegerId, von === '' ? null : von, bis === '' ? null : bis),
      holeAbrechnungen(traegerId),
    ])
      .then(([o, a]) => {
        if (!aktiv) return;
        setzeOffen(o);
        setzeBelege(a);
        setzeFehler(null);
      })
      .catch((u: unknown) => {
        if (aktiv) setzeFehler(alsFehler(u));
      });
    return () => {
      aktiv = false;
    };
  }, [traegerId, von, bis, zeitraumFalsch, stand]);

  useZaehler(traeger === null ? undefined : zaehlwort(offen.length, 'offener Gutschein', 'offene Gutscheine'));

  const gewaehlt = traeger?.find((t) => t.id === traegerId);
  const traegerName = gewaehlt?.name ?? '';
  const neuLaden = (meldung: string): void => {
    setzeFrage(null);
    setzeRueckmeldung(meldung);
    setzeStand((n) => n + 1);
  };

  const datei = (a: BgAbrechnungskopf, art: 'pdf' | 'csv'): void => {
    // Auftrag CRM-168: das PDF öffnet gleich im Betrachter, die CSV bleibt Download.
    ladeUndReiche(() => abrechnungsdatei(a.id, art), art === 'pdf')
      .then((hinweis) => {
        if (hinweis !== null) setzeRueckmeldung(hinweis);
      })
      .catch((u: unknown) => setzeFehler(alsFehler(u)));
  };

  return (
    <>
      {fehler !== null && <FehlerAnzeige fehler={fehler} />}
      {rueckmeldung !== '' && (
        <p className="hinweiszeile" role="status">
          {rueckmeldung}
        </p>
      )}
      {traeger !== null && traeger.length === 0 && (
        <p className="meldung meldung--hinweis" role="status">
          Kein Kunde ist als Kostenträger gekennzeichnet. Die Art des Kostenträgers wird in
          der Kundenakte gesetzt.
        </p>
      )}

      <Liste
        filter={
          <>
            <select
              className="liste__filter"
              aria-label="Kostenträger"
              value={traegerId}
              onChange={(e) => setzeTraegerId(e.target.value)}
            >
              <Traegeroptionen traeger={traeger ?? []} mitNummer />
            </select>
            <input
              type="date"
              className="liste__filter"
              aria-label="Termin von"
              value={von}
              onChange={(e) => setzeVon(e.target.value)}
            />
            <input
              type="date"
              className="liste__filter"
              aria-label="Termin bis"
              value={bis}
              aria-invalid={zeitraumFalsch}
              onChange={(e) => setzeBis(e.target.value)}
            />
          </>
        }
        hinweise={
          <p className="hilfe">
            {zeitraumFalsch && <strong>Der Zeitraum beginnt nach seinem Ende. </strong>}
            Angerechnete Gutscheine des gewählten Kostenträgers, die noch nicht abgerechnet sind. Ohne Zeitraum
            erscheinen alle offenen. Die Abrechnung trägt keine Umsatzsteuer: Die Leistung ist dem
            Teilnehmer voll versteuert in Rechnung gestellt.
          </p>
        }
      >
        <div className="tabellenhuelle">
          <table className="liste__tabelle">
            <caption className="nur-vorlesen">{offen.length} offene Gutscheine</caption>
            <thead>
              <tr>
                <th scope="col">Teilnehmer</th>
                <th scope="col">Gutschein-Nr.</th>
                <th scope="col">Name auf dem Gutschein</th>
                <th scope="col">Training</th>
                <th scope="col">Termin</th>
                <th scope="col">Rechnung</th>
                <th scope="col" className="zahl">
                  Betrag
                </th>
              </tr>
            </thead>
            <tbody>
              {offen.map((z) => (
                <tr key={z.gutschein}>
                  <td className="liste__name">{z.teilnehmer ?? '—'}</td>
                  <td className="liste__einzeilig">{z.nummer}</td>
                  <td>{z.name}</td>
                  <td>{z.leistung ?? '—'}</td>
                  <td className="liste__einzeilig">{alsTag(z.leistungstag) ?? '—'}</td>
                  <td className="liste__einzeilig">{z.rechnungsnummer ?? '—'}</td>
                  <td className="zahl">{alsEuro(z.betrag)}</td>
                </tr>
              ))}
              {offen.length > 0 && (
                <tr>
                  <th scope="row" colSpan={6}>
                    Summe
                  </th>
                  <td className="zahl">{alsEuro(summeDer(offen))}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {offen.length === 0 && traegerId !== '' && (
          <div className="liste__leer" role="status">
            <p>Kein offener Gutschein für diese Auswahl.</p>
          </div>
        )}
        <div className="knopfreihe">
          <button
            type="button"
            className="knopf knopf--haupt"
            disabled={offen.length === 0 || zeitraumFalsch}
            onClick={() => setzeFrage({ art: 'abrechnen' })}
          >
            Abrechnen …
          </button>
        </div>
      </Liste>

      <h2>
        Abrechnungen an {traegerName === '' ? 'den Kostenträger' : traegerName}
        {gewaehlt !== undefined && <small>{` · ${gewaehlt.artBezeichnung}`}</small>}
      </h2>
      <div className="tabellenhuelle">
        <table className="liste__tabelle">
          <caption className="nur-vorlesen">{belege.length} Abrechnungen</caption>
          <thead>
            <tr>
              <th scope="col">Beleg</th>
              <th scope="col">Datum</th>
              <th scope="col">Zeitraum</th>
              <th scope="col" className="zahl">
                Gutscheine
              </th>
              <th scope="col" className="zahl">
                Summe
              </th>
              <th scope="col">Stand</th>
              <th scope="col">
                <span className="nur-vorlesen">Handlungen</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {belege.map((a) => (
              <tr key={a.id}>
                <td className="liste__einzeilig">{a.belegnummer}</td>
                <td className="liste__einzeilig">{alsTag(a.belegdatum)}</td>
                <td className="liste__einzeilig">
                  {a.zeitraumVon === null && a.zeitraumBis === null
                    ? 'alle offenen'
                    : `${alsTag(a.zeitraumVon) ?? '…'} – ${alsTag(a.zeitraumBis) ?? '…'}`}
                </td>
                <td className="zahl">{a.anzahl}</td>
                <td className="zahl">{alsEuro(a.summe)}</td>
                <td>
                  <span
                    className={markenklasse(ABRECHNUNGSMARKE[a.status])}
                    title={a.stornogrund ?? (a.bezahltAm === null ? undefined : `bezahlt am ${alsTag(a.bezahltAm) ?? ''}`)}
                  >
                    {ABRECHNUNGSSTATUSNAME[a.status]}
                  </span>
                </td>
                <td className="liste__einzeilig">
                  <div className="knopfreihe">
                    <button type="button" className="knopf" onClick={() => datei(a, 'pdf')}>
                      PDF
                    </button>
                    <button type="button" className="knopf" onClick={() => datei(a, 'csv')}>
                      CSV
                    </button>
                    {a.status === 'abgerechnet' && (
                      <>
                        <button type="button" className="knopf" onClick={() => setzeFrage({ art: 'bezahlt', abrechnung: a })}>
                          Zahlung vermerken …
                        </button>
                        <button
                          type="button"
                          className="knopf knopf--eingriff"
                          onClick={() => setzeFrage({ art: 'storno', abrechnung: a })}
                        >
                          Stornieren …
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {belege.length === 0 && traegerId !== '' && (
        <p className="hilfe">Für diesen Kostenträger gibt es noch keine Abrechnung.</p>
      )}

      {frage?.art === 'abrechnen' && (
        <Rueckfrage
          frage="Gutscheine abrechnen?"
          unterzeile={gewaehlt === undefined ? traegerName : `${traegerName} (${gewaehlt.artBezeichnung})`}
          tat="Abrechnen"
          beiSchliessen={() => setzeFrage(null)}
          beiBestaetigen={async () => {
            const a = await abrechnen({ kostentraeger: traegerId, von: von === '' ? null : von, bis: bis === '' ? null : bis });
            neuLaden(`Abrechnung ${a.belegnummer} über ${alsEuro(a.summe)} erstellt (${a.anzahl} Gutscheine).`);
          }}
        >
          <p>
            {`${offen.length} Gutscheine über ${alsEuro(summeDer(offen))} gehen auf einen Abrechnungsbeleg mit eigener Nummer, ohne Umsatzsteuer. Die Teilnehmerrechnungen lassen sich danach nur noch stornieren, wenn die Abrechnung storniert ist.`}
          </p>
        </Rueckfrage>
      )}
      {frage?.art === 'bezahlt' && (
        <Zahlungsrueckfrage
          abrechnung={frage.abrechnung}
          beiSchliessen={() => setzeFrage(null)}
          beiErledigt={neuLaden}
        />
      )}
      {frage?.art === 'storno' && (
        <Rueckfrage
          frage="Abrechnung stornieren?"
          unterzeile={frage.abrechnung.belegnummer}
          tat="Stornieren"
          grund={{ beschriftung: 'Grund', hilfe: 'Steht am stornierten Beleg.' }}
          beiSchliessen={() => setzeFrage(null)}
          beiBestaetigen={async (grund) => {
            await stornieren(frage.abrechnung.id, grund);
            neuLaden(`${frage.abrechnung.belegnummer} storniert. Die Gutscheine sind wieder offen.`);
          }}
        >
          <p>Der Beleg bleibt lesbar; seine Gutscheine stehen wieder zur Abrechnung bereit.</p>
        </Rueckfrage>
      )}
    </>
  );
}

function Zahlungsrueckfrage({
  abrechnung,
  beiSchliessen,
  beiErledigt,
}: {
  readonly abrechnung: BgAbrechnungskopf;
  readonly beiSchliessen: () => void;
  readonly beiErledigt: (meldung: string) => void;
}): JSX.Element {
  const [am, setzeAm] = useState(heute());
  const amId = useId();
  return (
    <Rueckfrage
      frage="Zahlungseingang vermerken?"
      unterzeile={abrechnung.belegnummer}
      tat="Als bezahlt vermerken"
      gesperrt={am === ''}
      beiSchliessen={beiSchliessen}
      beiBestaetigen={async () => {
        await bezahlt(abrechnung.id, am);
        beiErledigt(`${abrechnung.belegnummer} als bezahlt vermerkt.`);
      }}
    >
      <p>{`Der Kostenträger hat ${alsEuro(abrechnung.summe)} gezahlt; die ${abrechnung.anzahl} Gutscheine gelten als bezahlt.`}</p>
      <div className="feld">
        <label className="feld__beschriftung" htmlFor={amId}>
          Eingang am <span className="feld__pflicht">Pflicht</span>
        </label>
        <input id={amId} type="date" className="feld__eingabe" value={am} onChange={(e) => setzeAm(e.target.value)} />
      </div>
    </Rueckfrage>
  );
}

/** Wozu ein Gutschein erfasst wird: eine Auftragsposition oder ein Teilnehmer. */
export type Gutscheinbezug =
  | {
      readonly art: 'auftrag';
      readonly id: string;
      readonly positionen: readonly { readonly id: string; readonly text: string }[];
    }
  | {
      readonly art: 'buchung';
      readonly id: string;
      readonly teilnehmer: readonly { readonly id: string; readonly name: string | null }[];
    };

/**
 * ============================================================================
 * GUTSCHEINE AN AUFTRAG ODER BUCHUNG (PRD-38, CRM-157, CRM-163)
 * ============================================================================
 *
 * Erfassen dürfen Service und Empfang. Angerechnet wird beim Festschreiben
 * der Rechnung; danach lässt sich hier nichts mehr erfassen (RNG-04).
 */
export function BgGutscheine({
  bezug,
  darfErfassen,
}: {
  readonly bezug: Gutscheinbezug;
  readonly darfErfassen: boolean;
}): JSX.Element | null {
  const [liste, setzeListe] = useState<readonly BgGutschein[]>([]);
  const [fehler, setzeFehler] = useState<ApiFehler | Error | null>(null);
  const [rueckmeldung, setzeRueckmeldung] = useState('');
  const [offenesFenster, setzeOffenesFenster] = useState(false);
  const [zuVerwerfen, setzeZuVerwerfen] = useState<BgGutschein | null>(null);
  const [stand, setzeStand] = useState(0);

  useEffect(() => {
    let aktiv = true;
    holeGutscheine(bezug.art === 'auftrag' ? { auftrag: bezug.id } : { buchung: bezug.id })
      .then((g) => {
        if (aktiv) setzeListe(g);
      })
      .catch((u: unknown) => {
        if (aktiv) setzeFehler(alsFehler(u));
      });
    return () => {
      aktiv = false;
    };
  }, [bezug.art, bezug.id, stand]);

  const zuordnung = (g: BgGutschein): string =>
    bezug.art === 'auftrag'
      ? (bezug.positionen.find((p) => p.id === g.auftragsposition)?.text ?? '—')
      : (g.teilnehmerName ?? '—');

  const ziele = bezug.art === 'auftrag' ? bezug.positionen.length : bezug.teilnehmer.length;
  if (liste.length === 0 && !darfErfassen) return null;

  return (
    <section className="karte" aria-label="Gutscheine">
      <h2>Gutscheine</h2>
      {fehler !== null && <FehlerAnzeige fehler={fehler} />}
      {rueckmeldung !== '' && (
        <p className="hinweiszeile" role="status">
          {rueckmeldung}
        </p>
      )}
      {liste.length > 0 && (
        <div className="tabellenhuelle">
          <table className="liste__tabelle">
            <thead>
              <tr>
                <th scope="col">Kostenträger</th>
                <th scope="col">Gutschein-Nr.</th>
                <th scope="col">Name</th>
                <th scope="col">{bezug.art === 'auftrag' ? 'Position' : 'Teilnehmer'}</th>
                <th scope="col" className="zahl">
                  Betrag
                </th>
                <th scope="col">Stand</th>
                <th scope="col">Rechnung</th>
                {darfErfassen && (
                  <th scope="col">
                    <span className="nur-vorlesen">Handlungen</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {liste.map((g) => (
                <tr key={g.id}>
                  <td>{g.kostentraegerName}</td>
                  <td className="liste__einzeilig">{g.nummer}</td>
                  <td>{g.name}</td>
                  <td>{zuordnung(g)}</td>
                  <td className="zahl">
                    {alsEuro(g.betrag)}
                    {/* CRM-163: der gewählte Wert aus der Liste des Kostenträgers. */}
                    {g.gutscheinwertBezeichnung !== null && (
                      <>
                        <br />
                        <span className="hilfe">{g.gutscheinwertBezeichnung}</span>
                      </>
                    )}
                  </td>
                  <td>
                    <span className={markenklasse(GUTSCHEINMARKE[g.status])}>
                      {GUTSCHEINSTATUSNAME[g.status]}
                      {g.abrechnungsnummer === null ? '' : ` (${g.abrechnungsnummer})`}
                    </span>
                  </td>
                  <td className="liste__einzeilig">{g.rechnungsnummer ?? '—'}</td>
                  {darfErfassen && (
                    <td>
                      {g.status === 'erfasst' && (
                        <button type="button" className="knopf knopf--eingriff" onClick={() => setzeZuVerwerfen(g)}>
                          Verwerfen …
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {darfErfassen && (
        <div className="knopfreihe">
          <button type="button" className="knopf" disabled={ziele === 0} onClick={() => setzeOffenesFenster(true)}>
            Gutschein erfassen …
          </button>
        </div>
      )}
      {offenesFenster && (
        <Erfassungsfenster
          bezug={bezug}
          beiSchliessen={() => setzeOffenesFenster(false)}
          beiErfasst={(meldung) => {
            setzeOffenesFenster(false);
            setzeRueckmeldung(meldung);
            setzeStand((n) => n + 1);
          }}
        />
      )}
      {zuVerwerfen !== null && (
        <Rueckfrage
          frage="Gutschein verwerfen?"
          unterzeile={`${zuVerwerfen.kostentraegerName}, Nr. ${zuVerwerfen.nummer}`}
          tat="Verwerfen"
          grund={{ beschriftung: 'Grund' }}
          beiSchliessen={() => setzeZuVerwerfen(null)}
          beiBestaetigen={async (grund) => {
            await verwerfen(zuVerwerfen.id, grund);
            setzeZuVerwerfen(null);
            setzeRueckmeldung(`Gutschein Nr. ${zuVerwerfen.nummer} verworfen.`);
            setzeStand((n) => n + 1);
          }}
        >
          <p>Der Gutschein wird nicht angerechnet. Dieselbe Nummer lässt sich danach neu erfassen.</p>
        </Rueckfrage>
      )}
    </section>
  );
}

/** Wo vorgemerkt wird: an den Positionen eines Angebots oder eines Auftrags (AeA-71). */
export type Vormerkungsbezug = {
  readonly art: 'angebot' | 'auftrag';
  readonly id: string;
  readonly positionen: readonly { readonly id: string; readonly text: string }[];
};

/**
 * ============================================================================
 * VORGEMERKTE GUTSCHEINE OHNE NAMEN (AeA-71, CRM-191)
 * ============================================================================
 *
 * Die Firma kündigt an: drei Teilnehmer kommen mit einem Gutschein der BG.
 * Vorgemerkt wird die Anzahl je Kostenträger und Wert — keine Namen, keine
 * Nummern. Jeder spätere Gutschein mit Nummer und Name zählt dagegen; die
 * Karte zeigt „n von m eingetragen". Angerechnet wird nur, was benannt ist
 * (PRF-17), und auf keinem Beleg steht eine Vormerkung.
 */
export function Gutscheinvormerkungen({
  bezug,
  darfVormerken,
}: {
  readonly bezug: Vormerkungsbezug;
  readonly darfVormerken: boolean;
}): JSX.Element | null {
  const [liste, setzeListe] = useState<readonly Gutscheinvormerkung[]>([]);
  const [fehler, setzeFehler] = useState<ApiFehler | Error | null>(null);
  const [rueckmeldung, setzeRueckmeldung] = useState('');
  const [fenster, setzeFenster] = useState<{ readonly art: 'neu' } | { readonly art: 'aendern'; readonly v: Gutscheinvormerkung } | null>(null);
  const [aufzuloesen, setzeAufzuloesen] = useState<Gutscheinvormerkung | null>(null);
  const [stand, setzeStand] = useState(0);

  useEffect(() => {
    let aktiv = true;
    holeVormerkungen(bezug.art === 'angebot' ? { angebot: bezug.id } : { auftrag: bezug.id })
      .then((v) => {
        if (aktiv) setzeListe(v);
      })
      .catch((u: unknown) => {
        if (aktiv) setzeFehler(alsFehler(u));
      });
    return () => {
      aktiv = false;
    };
  }, [bezug.art, bezug.id, stand]);

  if (liste.length === 0 && !darfVormerken) return null;

  const fertig = (meldung: string): void => {
    setzeFenster(null);
    setzeAufzuloesen(null);
    setzeRueckmeldung(meldung);
    setzeStand((n) => n + 1);
  };

  return (
    <section className="karte" aria-label="Vorgemerkte Gutscheine">
      <h2>Vorgemerkte Gutscheine</h2>
      <p className="hilfe">
        Angekündigte Gutscheine als Anzahl, noch ohne Namen. Angerechnet wird erst ein Gutschein mit Nummer und
        Name{bezug.art === 'angebot' ? ' im Auftrag' : ''}; die Übernahme in den Auftrag nimmt die Vormerkungen mit.
      </p>
      {fehler !== null && <FehlerAnzeige fehler={fehler} />}
      {rueckmeldung !== '' && (
        <p className="hinweiszeile" role="status">
          {rueckmeldung}
        </p>
      )}
      {liste.length > 0 && (
        <div className="tabellenhuelle">
          <table className="liste__tabelle">
            <thead>
              <tr>
                <th scope="col">Kostenträger</th>
                <th scope="col">Position</th>
                <th scope="col" className="zahl">
                  Betrag
                </th>
                <th scope="col">Eingetragen</th>
                <th scope="col">Bemerkung</th>
                {darfVormerken && (
                  <th scope="col">
                    <span className="nur-vorlesen">Handlungen</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {liste.map((v) => (
                <tr key={v.id}>
                  <td>{v.kostentraegerName}</td>
                  <td>{bezug.positionen.find((p) => p.id === (v.angebotsposition ?? v.auftragsposition))?.text ?? v.position}</td>
                  <td className="zahl">
                    {alsEuro(v.betrag)}
                    {v.gutscheinwertBezeichnung !== null && (
                      <>
                        <br />
                        <span className="hilfe">{v.gutscheinwertBezeichnung}</span>
                      </>
                    )}
                  </td>
                  <td className="liste__einzeilig">
                    <span className={markenklasse(v.offen === 0 ? 'abschluss' : 'laufend')}>
                      {v.eingetragen} von {v.anzahl} eingetragen
                    </span>
                  </td>
                  <td>{v.bemerkung ?? '—'}</td>
                  {darfVormerken && (
                    <td>
                      <div className="knopfreihe">
                        <button type="button" className="knopf knopf--klein" onClick={() => setzeFenster({ art: 'aendern', v })}>
                          Ändern …
                        </button>
                        {v.eingetragen === 0 && (
                          <button type="button" className="knopf knopf--klein knopf--eingriff" onClick={() => setzeAufzuloesen(v)}>
                            Auflösen …
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {darfVormerken && (
        <div className="knopfreihe">
          <button
            type="button"
            className="knopf"
            disabled={bezug.positionen.length === 0}
            onClick={() => setzeFenster({ art: 'neu' })}
          >
            Gutscheine vormerken …
          </button>
        </div>
      )}
      {fenster?.art === 'neu' && (
        <Vormerkfenster
          bezug={bezug}
          beiSchliessen={() => setzeFenster(null)}
          beiFertig={(v) => fertig(`${v.anzahl} × ${alsEuro(v.betrag)} ${v.kostentraegerName} vorgemerkt.`)}
        />
      )}
      {fenster?.art === 'aendern' && (
        <Vormerkungsaenderung
          vormerkung={fenster.v}
          beiSchliessen={() => setzeFenster(null)}
          beiFertig={(v) => fertig(`Vormerkung geändert: ${v.eingetragen} von ${v.anzahl} eingetragen.`)}
        />
      )}
      {aufzuloesen !== null && (
        <Rueckfrage
          frage="Vormerkung auflösen?"
          unterzeile={`${aufzuloesen.anzahl} × ${alsEuro(aufzuloesen.betrag)} ${aufzuloesen.kostentraegerName}`}
          tat="Auflösen"
          grund={{ beschriftung: 'Grund' }}
          beiSchliessen={() => setzeAufzuloesen(null)}
          beiBestaetigen={async (grund) => {
            await vormerkungAufloesen(aufzuloesen.id, grund);
            fertig('Vormerkung aufgelöst.');
          }}
        >
          {/* LOG-01: aufgelöst, nicht gelöscht — der Vorgang bleibt im Protokoll. */}
          <p>Die Vormerkung bleibt im Änderungsprotokoll stehen und zählt nicht mehr.</p>
        </Rueckfrage>
      )}
    </section>
  );
}

/** AeA-71: eine neue Vormerkung — Kostenträger, Wert aus der Liste oder eigener Betrag, Anzahl. */
function Vormerkfenster({
  bezug,
  beiSchliessen,
  beiFertig,
}: {
  readonly bezug: Vormerkungsbezug;
  readonly beiSchliessen: () => void;
  readonly beiFertig: (v: Gutscheinvormerkung) => void;
}): JSX.Element {
  const [traeger, setzeTraeger] = useState<readonly Kostentraeger[] | null>(null);
  const [traegerId, setzeTraegerId] = useState('');
  const [werte, setzeWerte] = useState<readonly Gutscheinwert[] | null>(null);
  const [wahl, setzeWahl] = useState('');
  const [position, setzePosition] = useState(bezug.positionen[0]?.id ?? '');
  const [betrag, setzeBetrag] = useState('');
  const [anzahl, setzeAnzahl] = useState('1');
  const [bemerkung, setzeBemerkung] = useState('');
  const [fehler, setzeFehler] = useState<ApiFehler | Error | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);
  const ids = { traeger: useId(), position: useId(), wert: useId(), betrag: useId(), anzahl: useId(), bemerkung: useId() };

  useEffect(() => {
    holeKostentraeger()
      .then((liste) => {
        setzeTraeger(liste);
        setzeTraegerId(liste[0]?.id ?? '');
      })
      .catch((u: unknown) => setzeFehler(alsFehler(u)));
  }, []);

  useEffect(() => {
    if (traegerId === '') return;
    let aktiv = true;
    holeWerte(traegerId)
      .then((liste) => {
        if (!aktiv) return;
        const gueltige = liste.filter((w) => w.gueltig);
        setzeWerte(gueltige);
        setzeWahl(vorwahl(gueltige));
      })
      .catch((u: unknown) => {
        if (!aktiv) return;
        setzeFehler(alsFehler(u));
        setzeWerte([]);
        setzeWahl(EIGENER_BETRAG);
      });
    return () => {
      aktiv = false;
    };
  }, [traegerId]);

  const gewaehlterWert = werte?.find((w) => w.id === wahl) ?? null;
  const mitEigenemBetrag = wahl === EIGENER_BETRAG;
  const betragstext = gueltigerBetrag(betrag);
  const betragFehler = betrag === '' || betragstext !== null ? null : BETRAGSHINWEIS;
  const zahl = /^\d{1,4}$/.test(anzahl.trim()) ? Number(anzahl.trim()) : 0;
  const vollstaendig =
    traegerId !== ''
    && position !== ''
    && zahl > 0
    && (gewaehlterWert !== null || (mitEigenemBetrag && betragstext !== null));

  const speichern = async (): Promise<void> => {
    if (!vollstaendig) return;
    setzeLaeuft(true);
    setzeFehler(null);
    try {
      beiFertig(
        await vormerken({
          angebotsposition: bezug.art === 'angebot' ? position : null,
          auftragsposition: bezug.art === 'auftrag' ? position : null,
          kostentraeger: traegerId,
          gutscheinwert: gewaehlterWert?.id ?? null,
          betrag: gewaehlterWert === null ? betragstext : null,
          anzahl: zahl,
          bemerkung: bemerkung.trim() === '' ? null : bemerkung.trim(),
        }),
      );
    } catch (u) {
      setzeFehler(alsFehler(u));
    } finally {
      setzeLaeuft(false);
    }
  };

  return (
    <Dialog
      titel="Gutscheine vormerken"
      unterzeile="Nur die Anzahl, ohne Namen. Angerechnet wird erst ein Gutschein mit Nummer und Name."
      breite="schmal"
      beiSchliessen={beiSchliessen}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void speichern();
        }}
      >
        {fehler !== null && <FehlerAnzeige fehler={fehler} />}
        {traeger !== null && traeger.length === 0 && (
          <p className="meldung meldung--hinweis">
            Kein Kunde ist als Kostenträger gekennzeichnet. Die Art des Kostenträgers wird in der Kundenakte gesetzt.
          </p>
        )}
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={ids.traeger}>
            Kostenträger <span className="feld__pflicht">Pflicht</span>
          </label>
          <select
            id={ids.traeger}
            className="feld__eingabe"
            value={traegerId}
            onChange={(e) => {
              setzeTraegerId(e.target.value);
              setzeWerte(null);
              setzeWahl('');
            }}
          >
            <Traegeroptionen traeger={traeger ?? []} />
          </select>
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={ids.position}>
            Position <span className="feld__pflicht">Pflicht</span>
          </label>
          <select id={ids.position} className="feld__eingabe" value={position} onChange={(e) => setzePosition(e.target.value)}>
            {bezug.positionen.map((p) => (
              <option key={p.id} value={p.id}>
                {p.text}
              </option>
            ))}
          </select>
        </div>
        {traegerId !== '' && werte === null && <p className="hinweiszeile">Gutscheinwerte werden geladen …</p>}
        {werte !== null && werte.length > 0 && (
          <div className="feld">
            <label className="feld__beschriftung" htmlFor={ids.wert}>
              Gutscheinwert <span className="feld__pflicht">Pflicht</span>
            </label>
            <select id={ids.wert} className="feld__eingabe" value={wahl} onChange={(e) => setzeWahl(e.target.value)}>
              {wahl === '' && (
                <option value="" disabled>
                  bitte wählen
                </option>
              )}
              {werte.map((w) => (
                <option key={w.id} value={w.id}>
                  {`${w.bezeichnung} – ${alsEuro(w.betrag)}`}
                </option>
              ))}
              <option value={EIGENER_BETRAG}>eigener Betrag</option>
            </select>
          </div>
        )}
        {mitEigenemBetrag && (
          <div className="feld">
            <label className="feld__beschriftung" htmlFor={ids.betrag}>
              Betrag je Gutschein <span className="feld__pflicht">Pflicht</span>
            </label>
            <input
              id={ids.betrag}
              className="feld__eingabe"
              inputMode="decimal"
              value={betrag}
              aria-invalid={betragFehler !== null}
              onChange={(e) => setzeBetrag(e.target.value)}
            />
            {betragFehler !== null && <p className="feld__hilfe">{betragFehler}</p>}
          </div>
        )}
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={ids.anzahl}>
            Anzahl <span className="feld__pflicht">Pflicht</span>
          </label>
          <input
            id={ids.anzahl}
            className="feld__eingabe"
            inputMode="numeric"
            value={anzahl}
            aria-invalid={zahl === 0}
            onChange={(e) => setzeAnzahl(e.target.value)}
          />
          <p className="feld__hilfe">Bei Plätzen höchstens die Teilnehmerzahl der Position.</p>
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={ids.bemerkung}>
            Bemerkung
          </label>
          <input id={ids.bemerkung} className="feld__eingabe" value={bemerkung} maxLength={500} onChange={(e) => setzeBemerkung(e.target.value)} />
        </div>
        <div className="knopfreihe">
          <button type="button" className="knopf" onClick={beiSchliessen} disabled={laeuft}>
            Abbrechen
          </button>
          <button type="submit" className="knopf knopf--haupt" disabled={!vollstaendig || laeuft}>
            Vormerken
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/** AeA-71: Anzahl und Bemerkung ändern; Kostenträger und Betrag bleiben, nie unter das Eingetragene. */
function Vormerkungsaenderung({
  vormerkung,
  beiSchliessen,
  beiFertig,
}: {
  readonly vormerkung: Gutscheinvormerkung;
  readonly beiSchliessen: () => void;
  readonly beiFertig: (v: Gutscheinvormerkung) => void;
}): JSX.Element {
  const [anzahl, setzeAnzahl] = useState(String(vormerkung.anzahl));
  const [bemerkung, setzeBemerkung] = useState(vormerkung.bemerkung ?? '');
  const [fehler, setzeFehler] = useState<ApiFehler | Error | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);
  const ids = { anzahl: useId(), bemerkung: useId() };
  const zahl = /^\d{1,4}$/.test(anzahl.trim()) ? Number(anzahl.trim()) : 0;
  const zulaessig = zahl > 0 && zahl >= vormerkung.eingetragen;

  const speichern = async (): Promise<void> => {
    if (!zulaessig) return;
    setzeLaeuft(true);
    setzeFehler(null);
    try {
      beiFertig(
        await vormerkungAendern(vormerkung.id, {
          anzahl: zahl,
          bemerkung: bemerkung.trim() === '' ? null : bemerkung.trim(),
        }),
      );
    } catch (u) {
      setzeFehler(alsFehler(u));
    } finally {
      setzeLaeuft(false);
    }
  };

  return (
    <Dialog
      titel="Vormerkung ändern"
      unterzeile={`${vormerkung.kostentraegerName}, ${alsEuro(vormerkung.betrag)} je Gutschein`}
      breite="schmal"
      beiSchliessen={beiSchliessen}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void speichern();
        }}
      >
        {fehler !== null && <FehlerAnzeige fehler={fehler} />}
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={ids.anzahl}>
            Anzahl <span className="feld__pflicht">Pflicht</span>
          </label>
          <input
            id={ids.anzahl}
            className="feld__eingabe"
            inputMode="numeric"
            value={anzahl}
            aria-invalid={!zulaessig}
            onChange={(e) => setzeAnzahl(e.target.value)}
          />
          <p className="feld__hilfe">
            {vormerkung.eingetragen > 0
              ? `Mindestens ${vormerkung.eingetragen} — so viele Gutscheine sind schon eingetragen.`
              : 'Ganz zurücknehmen: „Auflösen“ in der Liste.'}
          </p>
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={ids.bemerkung}>
            Bemerkung
          </label>
          <input id={ids.bemerkung} className="feld__eingabe" value={bemerkung} maxLength={500} onChange={(e) => setzeBemerkung(e.target.value)} />
        </div>
        <div className="knopfreihe">
          <button type="button" className="knopf" onClick={beiSchliessen} disabled={laeuft}>
            Abbrechen
          </button>
          <button type="submit" className="knopf knopf--haupt" disabled={!zulaessig || laeuft}>
            Sichern
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/** Die Wahl „eigener Betrag" in der Auswahl der Gutscheinwerte (keine UUID, also nie ein Wert). */
const EIGENER_BETRAG = 'eigen';

/**
 * PRD-38 (CRM-163): Welche Wahl nach dem Laden der Werte vorbelegt ist. Genau
 * ein gültiger Wert steht schon da — der Regelfall am Tresen, ein Klick
 * weniger. Gibt es keinen, ist der eigene Betrag die einzige Möglichkeit und
 * keine Wahl. Bei mehreren entscheidet der Mensch: Ein vorgewählter erster
 * Wert würde bei einer Unachtsamkeit still den falschen Betrag anrechnen.
 */
function vorwahl(gueltige: readonly Gutscheinwert[]): string {
  if (gueltige.length === 0) return EIGENER_BETRAG;
  return gueltige.length === 1 ? (gueltige[0]?.id ?? '') : '';
}

function Erfassungsfenster({
  bezug,
  beiSchliessen,
  beiErfasst,
}: {
  readonly bezug: Gutscheinbezug;
  readonly beiSchliessen: () => void;
  readonly beiErfasst: (meldung: string) => void;
}): JSX.Element {
  const ziele =
    bezug.art === 'auftrag'
      ? bezug.positionen.map((p) => ({ id: p.id, text: p.text, name: null }))
      : bezug.teilnehmer.map((t) => ({ id: t.id, text: t.name ?? 'Teilnehmer ohne Namen', name: t.name }));
  const [traeger, setzeTraeger] = useState<readonly Kostentraeger[] | null>(null);
  const [traegerId, setzeTraegerId] = useState('');
  /** Die heute gültigen Werte des gewählten Kostenträgers; null, solange sie laden. */
  const [werte, setzeWerte] = useState<readonly Gutscheinwert[] | null>(null);
  /** Die id eines Werts, `EIGENER_BETRAG` oder '' (noch nichts gewählt). */
  const [wahl, setzeWahl] = useState('');
  const [ziel, setzeZiel] = useState(ziele[0]?.id ?? '');
  const [nummer, setzeNummer] = useState('');
  const [name, setzeName] = useState(ziele[0]?.name ?? '');
  const [betrag, setzeBetrag] = useState('');
  /** AeA-71: die offenen Vormerkungen am gewählten Ziel und die gewählte ('' = keine). */
  const [passende, setzePassende] = useState<readonly Gutscheinvormerkung[]>([]);
  const [vormerkungId, setzeVormerkungId] = useState('');
  const [fehler, setzeFehler] = useState<ApiFehler | Error | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);
  const ids = {
    vormerkung: useId(),
    traeger: useId(),
    ziel: useId(),
    nummer: useId(),
    name: useId(),
    wert: useId(),
    betrag: useId(),
  };

  useEffect(() => {
    holeKostentraeger()
      .then((liste) => {
        setzeTraeger(liste);
        // Eine schon vorbelegte Vormerkung (AeA-71) bestimmt den Kostenträger.
        setzeTraegerId((alt) => (alt !== '' ? alt : (liste[0]?.id ?? '')));
      })
      .catch((u: unknown) => setzeFehler(alsFehler(u)));
  }, []);

  // AeA-71: Ist genau eine Vormerkung am Ziel offen, ist sie gemeint; bei mehreren wählt der Mensch.
  useEffect(() => {
    if (ziel === '') return;
    let aktiv = true;
    holePassendeVormerkungen(bezug.art === 'auftrag' ? { auftragsposition: ziel } : { teilnehmer: ziel })
      .then((liste) => {
        if (!aktiv) return;
        setzePassende(liste);
        const einzige = liste.length === 1 ? (liste[0] ?? null) : null;
        setzeVormerkungId(einzige?.id ?? '');
        // Die Werte des Kostenträgers laden nach; der Vorbeleg unten setzt danach den Wert.
        if (einzige !== null) setzeTraegerId(einzige.kostentraeger);
      })
      .catch((u: unknown) => {
        if (!aktiv) return;
        // Ohne Vormerkungen geht die Erfassung wie bisher; der Fehler bleibt sichtbar.
        setzeFehler(alsFehler(u));
        setzePassende([]);
      });
    return () => {
      aktiv = false;
    };
  }, [bezug.art, ziel]);

  // PRD-38 (CRM-163): Zum gewählten Kostenträger nur die heute gültigen Werte.
  useEffect(() => {
    if (traegerId === '') return;
    let aktiv = true;
    holeWerte(traegerId)
      .then((liste) => {
        if (!aktiv) return;
        const gueltige = liste.filter((w) => w.gueltig);
        setzeWerte(gueltige);
        setzeWahl(vorwahl(gueltige));
      })
      .catch((u: unknown) => {
        if (!aktiv) return;
        // Ohne Liste bleibt der eigene Betrag — die Erfassung soll daran nicht scheitern.
        setzeFehler(alsFehler(u));
        setzeWerte([]);
        setzeWahl(EIGENER_BETRAG);
      });
    return () => {
      aktiv = false;
    };
  }, [traegerId]);

  const gewaehlteVormerkung = passende.find((v) => v.id === vormerkungId) ?? null;

  // AeA-71: Die gewählte Vormerkung belegt den Wert vor — ihr Listeneintrag, sonst ihr Betrag.
  useEffect(() => {
    if (gewaehlteVormerkung === null || werte === null || gewaehlteVormerkung.kostentraeger !== traegerId) return;
    const wert = werte.find((w) => w.id === gewaehlteVormerkung.gutscheinwert);
    if (wert === undefined) {
      setzeWahl(EIGENER_BETRAG);
      setzeBetrag(alsEingabebetrag(gewaehlteVormerkung.betrag));
    } else {
      setzeWahl(wert.id);
    }
  }, [gewaehlteVormerkung, werte, traegerId]);

  const gewaehlterWert = werte?.find((w) => w.id === wahl) ?? null;
  const mitEigenemBetrag = wahl === EIGENER_BETRAG;
  const betragstext = gueltigerBetrag(betrag);
  const betragFehler = betrag === '' || betragstext !== null ? null : BETRAGSHINWEIS;
  const vollstaendig =
    traegerId !== ''
    && ziel !== ''
    && nummer.trim() !== ''
    && (gewaehlterWert !== null || (mitEigenemBetrag && betragstext !== null));

  const speichern = async (): Promise<void> => {
    if (!vollstaendig) return;
    setzeLaeuft(true);
    setzeFehler(null);
    try {
      const ergebnis = await erfassen({
        kostentraeger: traegerId,
        nummer: nummer.trim(),
        name: name.trim() === '' ? null : name.trim(),
        // PRD-38 (CRM-163): mit Wert nimmt der Server dessen Betrag, sonst der eigene.
        gutscheinwert: gewaehlterWert?.id ?? null,
        betrag: gewaehlterWert === null ? betragstext : null,
        teilnehmer: bezug.art === 'buchung' ? ziel : null,
        auftragsposition: bezug.art === 'auftrag' ? ziel : null,
        vormerkung: vormerkungId === '' ? null : vormerkungId,
      });
      beiErfasst(
        `Gutschein Nr. ${ergebnis.gutschein.nummer} über ${alsEuro(ergebnis.gutschein.betrag)} erfasst.`
          + (ergebnis.gutschein.vormerkung === null ? '' : ' Er zählt gegen die Vormerkung.')
          + (ergebnis.hinweis === null ? '' : ` Hinweis: ${ergebnis.hinweis}`),
      );
    } catch (u) {
      setzeFehler(alsFehler(u));
    } finally {
      setzeLaeuft(false);
    }
  };

  return (
    <Dialog titel="Gutschein erfassen" unterzeile="Der Zuschuss mindert den Zahlbetrag der Rechnung." breite="schmal" beiSchliessen={beiSchliessen}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void speichern();
        }}
      >
        {fehler !== null && <FehlerAnzeige fehler={fehler} />}
        {traeger !== null && traeger.length === 0 && (
          <p className="meldung meldung--hinweis">
            Kein Kunde ist als Kostenträger gekennzeichnet. Die Art des Kostenträgers wird in der Kundenakte gesetzt.
          </p>
        )}
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={ids.traeger}>
            Kostenträger <span className="feld__pflicht">Pflicht</span>
          </label>
          <select
            id={ids.traeger}
            className="feld__eingabe"
            value={traegerId}
            onChange={(e) => {
              setzeTraegerId(e.target.value);
              setzeWerte(null);
              setzeWahl('');
              // Ein anderer Kostenträger passt nicht mehr zur gewählten Vormerkung.
              if (gewaehlteVormerkung?.kostentraeger !== e.target.value) setzeVormerkungId('');
            }}
          >
            <Traegeroptionen traeger={traeger ?? []} />
          </select>
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={ids.ziel}>
            {bezug.art === 'auftrag' ? 'Position' : 'Teilnehmer'} <span className="feld__pflicht">Pflicht</span>
          </label>
          <select
            id={ids.ziel}
            className="feld__eingabe"
            value={ziel}
            onChange={(e) => {
              const neu = ziele.find((z) => z.id === e.target.value);
              setzeZiel(e.target.value);
              // Der Name folgt dem Teilnehmer, solange niemand ihn geändert hat.
              if (neu?.name != null && (name === '' || ziele.some((z) => z.name === name))) setzeName(neu.name);
            }}
          >
            {ziele.map((z) => (
              <option key={z.id} value={z.id}>
                {z.text}
              </option>
            ))}
          </select>
        </div>
        {passende.length > 0 && (
          <div className="feld">
            <label className="feld__beschriftung" htmlFor={ids.vormerkung}>
              Vorgemerkt
            </label>
            <select
              id={ids.vormerkung}
              className="feld__eingabe"
              value={vormerkungId}
              onChange={(e) => {
                setzeVormerkungId(e.target.value);
                const v = passende.find((p) => p.id === e.target.value);
                if (v !== undefined) setzeTraegerId(v.kostentraeger);
              }}
            >
              <option value="">{passende.length > 1 ? 'bitte wählen oder ohne Vormerkung' : 'ohne Vormerkung'}</option>
              {passende.map((v) => (
                <option key={v.id} value={v.id}>
                  {`${v.kostentraegerName} – ${alsEuro(v.betrag)} (${v.eingetragen} von ${v.anzahl} eingetragen)`}
                </option>
              ))}
            </select>
            <p className="feld__hilfe">Der Gutschein zählt gegen die Vormerkung; Kostenträger und Wert sind vorbelegt.</p>
          </div>
        )}
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={ids.nummer}>
            Gutscheinnummer <span className="feld__pflicht">Pflicht</span>
          </label>
          <input id={ids.nummer} className="feld__eingabe" value={nummer} maxLength={60} onChange={(e) => setzeNummer(e.target.value)} />
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={ids.name}>
            Name auf dem Gutschein
          </label>
          <input id={ids.name} className="feld__eingabe" value={name} maxLength={200} onChange={(e) => setzeName(e.target.value)} />
          <p className="feld__hilfe">Leer: der Name des Teilnehmers. Weicht er ab, erscheint ein Hinweis.</p>
        </div>
        {traegerId !== '' && werte === null && <p className="hinweiszeile">Gutscheinwerte werden geladen …</p>}
        {werte !== null && werte.length > 0 && (
          <div className="feld">
            <label className="feld__beschriftung" htmlFor={ids.wert}>
              Gutscheinwert <span className="feld__pflicht">Pflicht</span>
            </label>
            <select id={ids.wert} className="feld__eingabe" value={wahl} onChange={(e) => setzeWahl(e.target.value)}>
              {wahl === '' && (
                <option value="" disabled>
                  bitte wählen
                </option>
              )}
              {werte.map((w) => (
                <option key={w.id} value={w.id}>
                  {`${w.bezeichnung} – ${alsEuro(w.betrag)}`}
                </option>
              ))}
              <option value={EIGENER_BETRAG}>eigener Betrag</option>
            </select>
          </div>
        )}
        {mitEigenemBetrag && (
          <div className="feld">
            <label className="feld__beschriftung" htmlFor={ids.betrag}>
              Betrag <span className="feld__pflicht">Pflicht</span>
            </label>
            <input
              id={ids.betrag}
              className="feld__eingabe"
              inputMode="decimal"
              value={betrag}
              aria-invalid={betragFehler !== null}
              onChange={(e) => setzeBetrag(e.target.value)}
            />
            <p className="feld__hilfe">
              {betragFehler ?? 'Mehrere Gutscheine je Position sind möglich, zusammen höchstens der Bruttobetrag der Position.'}
            </p>
          </div>
        )}
        <div className="knopfreihe">
          <button type="button" className="knopf" onClick={beiSchliessen} disabled={laeuft}>
            Abbrechen
          </button>
          <button type="submit" className="knopf knopf--haupt" disabled={!vollstaendig || laeuft}>
            Erfassen
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * PRD-38 (CRM-162, CRM-163): Wer die Art des Kostenträgers und seine
 * Gutscheinwerte setzen darf — wer Kunden anlegen oder ändern darf. Der Server
 * verlangt dasselbe (`@Recht('kunde', 'aendern', 'anlegen')`).
 */
export function darfBgKennzeichenSetzen(darf: (objekt: string, aktion: string) => boolean): boolean {
  return darf('kunde', 'aendern') || darf('kunde', 'anlegen');
}

/**
 * ============================================================================
 * DIE ZEILE „KOSTENTRÄGER" IN DER KUNDENAKTE (PRD-38, E-74, CRM-162, CRM-163)
 * ============================================================================
 *
 * Statt eines Ja/Nein steht die Art aus der Werteliste (E-74) — „nein" nimmt
 * das Kennzeichen weg. Setzen darf es, wer Kunden anlegen oder ändern darf;
 * ein eigenes Recht gibt es dafür nicht. Privatkunden tragen es nicht — das
 * prüft der Dienst.
 *
 * Angeboten werden die Arten in Gebrauch. Trägt der Kunde eine Art, die
 * inzwischen außer Gebrauch ist, bleibt sie als eigene Zeile wählbar — sonst
 * zeigte die Auswahl stillschweigend etwas anderes, als gilt.
 *
 * Die Gutscheinwerte stehen seit CRM-171 im eigenen Reiter der Akte
 * (`Gutscheinwerte`); hier bleibt nur die Art. Nach dem Setzen holt die Akte
 * sich neu (`beiGeaendert`) — Kennzeichnung und Reiter folgen der Art.
 */
export function Kostentraegerzeile({
  kunde,
  darfVerwalten,
  beiMeldung,
  beiGeaendert,
}: {
  readonly kunde: string;
  readonly darfVerwalten: boolean;
  readonly beiMeldung: (meldung: string) => void;
  readonly beiGeaendert?: () => void;
}): JSX.Element {
  /** undefined: nicht geladen; null: kein Kostenträger; sonst der Schlüssel der Art. */
  const [art, setzeArt] = useState<string | null | undefined>(undefined);
  const [arten, setzeArten] = useState<readonly Kostentraegerart[]>([]);

  useEffect(() => {
    let aktiv = true;
    Promise.all([holeKostentraeger(), holeArten()])
      .then(([liste, werteliste]) => {
        if (!aktiv) return;
        setzeArten(werteliste);
        setzeArt(liste.find((t) => t.id === kunde)?.art ?? null);
      })
      .catch(() => {
        if (aktiv) setzeArt(undefined);
      });
    return () => {
      aktiv = false;
    };
  }, [kunde]);

  const optionen = [
    { wert: '', text: 'nein' },
    ...arten
      .filter((a) => a.aktiv || a.schluessel === art)
      .map((a) => ({ wert: a.schluessel, text: a.aktiv ? a.bezeichnung : `${a.bezeichnung} (außer Gebrauch)` })),
  ];

  const setzen = async (neu: string | null): Promise<void> => {
    try {
      await kostentraegerSetzen(kunde, neu);
      setzeArt(neu);
      const bezeichnung = arten.find((a) => a.schluessel === neu)?.bezeichnung ?? neu;
      beiMeldung(neu === null ? 'Der Kunde ist kein Kostenträger mehr.' : `Der Kunde ist Kostenträger: ${bezeichnung ?? ''}.`);
      beiGeaendert?.();
    } catch (u) {
      beiMeldung(u instanceof Error ? u.message : String(u));
    }
  };

  return (
    <Feldzeile
      beschriftung="Kostenträger"
      kennung="akte-kostentraeger"
      wert={art === undefined ? null : (art ?? '')}
      {...(art === undefined ? {} : { optionen })}
      {...(darfVerwalten && art !== undefined ? { beiSichern: setzen } : {})}
    />
  );
}

type Wertfrage =
  | { readonly art: 'anlegen' }
  | { readonly art: 'aendern'; readonly wert: Gutscheinwert }
  | { readonly art: 'loeschen'; readonly wert: Gutscheinwert };

/** Aktiv, veraltet (aus der Liste genommen) oder heute außerhalb der Gültigkeit. */
function wertstand(w: Gutscheinwert): { readonly text: string; readonly marke: Markenart } {
  if (!w.aktiv) return { text: 'veraltet', marke: 'stillgelegt' };
  if (!w.gueltig) return { text: 'nicht gültig', marke: 'aufmerksamkeit' };
  return { text: 'aktiv', marke: 'laufend' };
}

/**
 * ============================================================================
 * DIE GUTSCHEINWERTE EINES KOSTENTRÄGERS (PRD-38, CRM-163)
 * ============================================================================
 *
 * Feste Beträge, die bei der Erfassung zur Wahl stehen — „Standard 120 €",
 * „Aufbaukurs 90 €". Ein Wert, der schon auf einem Gutschein steht, wird nicht
 * gelöscht, sondern als veraltet markiert: Er wird nicht mehr angeboten, und
 * der Gutschein zeigt weiter, woher sein Betrag kam. Den Knopf „Löschen" gibt
 * es deshalb nur bei einem nie benutzten Wert.
 *
 * Seit CRM-171 der Inhalt des Reiters „Gutscheinwerte" der Kundenakte; die
 * Überschrift setzt die Akte. `beiListe` meldet jede geladene Liste, damit
 * der Zähler am Reiter nach einer Änderung stimmt.
 */
export function Gutscheinwerte({
  kunde,
  darfVerwalten,
  beiListe,
}: {
  readonly kunde: string;
  readonly darfVerwalten: boolean;
  readonly beiListe?: (liste: readonly Gutscheinwert[]) => void;
}): JSX.Element {
  const [liste, setzeListe] = useState<readonly Gutscheinwert[] | null>(null);
  const [fehler, setzeFehler] = useState<ApiFehler | Error | null>(null);
  const [rueckmeldung, setzeRueckmeldung] = useState('');
  const [frage, setzeFrage] = useState<Wertfrage | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);
  const [stand, setzeStand] = useState(0);

  useEffect(() => {
    let aktiv = true;
    holeWerte(kunde)
      .then((w) => {
        if (!aktiv) return;
        setzeListe(w);
        beiListe?.(w);
      })
      .catch((u: unknown) => {
        if (aktiv) setzeFehler(alsFehler(u));
      });
    return () => {
      aktiv = false;
    };
  }, [kunde, stand, beiListe]);

  const fertig = (meldung: string): void => {
    setzeFrage(null);
    setzeFehler(null);
    setzeRueckmeldung(meldung);
    setzeStand((n) => n + 1);
  };

  const umschalten = (w: Gutscheinwert): void => {
    setzeLaeuft(true);
    setzeFehler(null);
    wertAendern(w.id, {
      bezeichnung: w.bezeichnung,
      betrag: w.betrag,
      gueltigAb: w.gueltigAb,
      gueltigBis: w.gueltigBis,
      aktiv: !w.aktiv,
    })
      .then(() =>
        fertig(
          w.aktiv
            ? `„${w.bezeichnung}" ist als veraltet markiert und wird nicht mehr angeboten.`
            : `„${w.bezeichnung}" wird wieder angeboten.`,
        ),
      )
      .catch((u: unknown) => setzeFehler(alsFehler(u)))
      .finally(() => setzeLaeuft(false));
  };

  return (
    <div>
      <p className="hilfe">
        Feste Beträge, die bei der Erfassung eines Gutscheins zur Wahl stehen. Ein veralteter Wert
        wird nicht mehr angeboten; Gutscheine, die ihn schon tragen, behalten ihn.
      </p>
      {fehler !== null && <FehlerAnzeige fehler={fehler} />}
      {rueckmeldung !== '' && (
        <p className="hinweiszeile" role="status">
          {rueckmeldung}
        </p>
      )}
      {liste !== null && liste.length === 0 && (
        <p className="hilfe">Noch kein Gutscheinwert hinterlegt; erfasst wird dann mit eigenem Betrag.</p>
      )}
      {liste !== null && liste.length > 0 && (
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle" aria-label="Gutscheinwerte">
            <thead>
              <tr>
                <th scope="col">Bezeichnung</th>
                <th scope="col" className="zahl">
                  Betrag
                </th>
                <th scope="col">Gültig ab</th>
                <th scope="col">Gültig bis</th>
                <th scope="col">Stand</th>
                <th scope="col" className="zahl">
                  Benutzt
                </th>
                {darfVerwalten && <th scope="col">Handlung</th>}
              </tr>
            </thead>
            <tbody>
              {liste.map((w) => {
                const s = wertstand(w);
                return (
                  <tr key={w.id} className={w.aktiv ? undefined : 'liste__zeile--blass'}>
                    <th scope="row">{w.bezeichnung}</th>
                    <td className="zahl">{alsEuro(w.betrag)}</td>
                    <td className="liste__einzeilig">{alsTag(w.gueltigAb) ?? '—'}</td>
                    <td className="liste__einzeilig">{alsTag(w.gueltigBis) ?? '—'}</td>
                    <td>
                      <span className={markenklasse(s.marke)}>{s.text}</span>
                    </td>
                    <td className="zahl">{w.benutzt}</td>
                    {darfVerwalten && (
                      <td>
                        <div className="knopfreihe">
                          <button
                            type="button"
                            className="knopf knopf--klein"
                            disabled={laeuft}
                            onClick={() => setzeFrage({ art: 'aendern', wert: w })}
                          >
                            Ändern …
                          </button>
                          <button
                            type="button"
                            className="knopf knopf--klein"
                            disabled={laeuft}
                            onClick={() => umschalten(w)}
                          >
                            {w.aktiv ? 'Als veraltet markieren' : 'Wieder aktiv'}
                          </button>
                          {w.benutzt === 0 && (
                            <button
                              type="button"
                              className="knopf knopf--klein knopf--eingriff"
                              disabled={laeuft}
                              onClick={() => setzeFrage({ art: 'loeschen', wert: w })}
                            >
                              Löschen …
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {darfVerwalten && (
        <div className="knopfreihe">
          <button type="button" className="knopf knopf--klein" onClick={() => setzeFrage({ art: 'anlegen' })}>
            Gutscheinwert anlegen …
          </button>
        </div>
      )}
      {(frage?.art === 'anlegen' || frage?.art === 'aendern') && (
        <Wertfenster
          kunde={kunde}
          wert={frage.art === 'aendern' ? frage.wert : null}
          beiSchliessen={() => setzeFrage(null)}
          beiFertig={fertig}
        />
      )}
      {frage?.art === 'loeschen' && (
        <Rueckfrage
          frage="Gutscheinwert löschen?"
          unterzeile={`${frage.wert.bezeichnung} – ${alsEuro(frage.wert.betrag)}`}
          tat="Löschen"
          beiSchliessen={() => setzeFrage(null)}
          beiBestaetigen={async () => {
            await wertLoeschen(frage.wert.id);
            fertig(`Gutscheinwert „${frage.wert.bezeichnung}" gelöscht.`);
          }}
        >
          <p>
            Der Wert steht auf keinem Gutschein und verschwindet aus der Liste. Ein benutzter Wert
            lässt sich nicht löschen, nur als veraltet markieren.
          </p>
        </Rueckfrage>
      )}
    </div>
  );
}

/** Anlegen oder Ändern eines Gutscheinwerts (PRD-38, CRM-163). */
function Wertfenster({
  kunde,
  wert,
  beiSchliessen,
  beiFertig,
}: {
  readonly kunde: string;
  /** null: ein neuer Wert. */
  readonly wert: Gutscheinwert | null;
  readonly beiSchliessen: () => void;
  readonly beiFertig: (meldung: string) => void;
}): JSX.Element {
  const [bezeichnung, setzeBezeichnung] = useState(wert?.bezeichnung ?? '');
  const [betrag, setzeBetrag] = useState(wert === null ? '' : alsEingabebetrag(wert.betrag));
  const [ab, setzeAb] = useState(wert?.gueltigAb ?? '');
  const [bis, setzeBis] = useState(wert?.gueltigBis ?? '');
  const [fehler, setzeFehler] = useState<ApiFehler | Error | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);
  const ids = { bezeichnung: useId(), betrag: useId(), ab: useId(), bis: useId() };

  const betragstext = gueltigerBetrag(betrag);
  const betragFehler = betrag === '' || betragstext !== null ? null : BETRAGSHINWEIS;
  const zeitraumFalsch = ab !== '' && bis !== '' && ab > bis;
  const vollstaendig = bezeichnung.trim() !== '' && betragstext !== null && !zeitraumFalsch;

  const speichern = async (): Promise<void> => {
    if (!vollstaendig || betragstext === null) return;
    setzeLaeuft(true);
    setzeFehler(null);
    const eingabe: GutscheinwertEingabe = {
      bezeichnung: bezeichnung.trim(),
      betrag: betragstext,
      gueltigAb: ab === '' ? null : ab,
      gueltigBis: bis === '' ? null : bis,
      aktiv: wert?.aktiv ?? true,
    };
    try {
      if (wert === null) {
        const neu = await wertAnlegen(kunde, eingabe);
        beiFertig(`Gutscheinwert „${neu.bezeichnung}" über ${alsEuro(neu.betrag)} angelegt.`);
      } else {
        const geaendert = await wertAendern(wert.id, eingabe);
        beiFertig(`Gutscheinwert „${geaendert.bezeichnung}" geändert.`);
      }
    } catch (u) {
      setzeFehler(alsFehler(u));
    } finally {
      setzeLaeuft(false);
    }
  };

  return (
    <Dialog
      titel={wert === null ? 'Gutscheinwert anlegen' : 'Gutscheinwert ändern'}
      unterzeile="Ein fester Betrag, der bei der Erfassung eines Gutscheins zur Wahl steht."
      breite="schmal"
      beiSchliessen={beiSchliessen}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void speichern();
        }}
      >
        {fehler !== null && <FehlerAnzeige fehler={fehler} />}
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={ids.bezeichnung}>
            Bezeichnung <span className="feld__pflicht">Pflicht</span>
          </label>
          <input
            id={ids.bezeichnung}
            className="feld__eingabe"
            value={bezeichnung}
            maxLength={120}
            onChange={(e) => setzeBezeichnung(e.target.value)}
          />
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={ids.betrag}>
            Betrag <span className="feld__pflicht">Pflicht</span>
          </label>
          <input
            id={ids.betrag}
            className="feld__eingabe"
            inputMode="decimal"
            value={betrag}
            aria-invalid={betragFehler !== null}
            onChange={(e) => setzeBetrag(e.target.value)}
          />
          {betragFehler !== null && <p className="feld__hilfe">{betragFehler}</p>}
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={ids.ab}>
            Gültig ab
          </label>
          <input id={ids.ab} type="date" className="feld__eingabe" value={ab} onChange={(e) => setzeAb(e.target.value)} />
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={ids.bis}>
            Gültig bis
          </label>
          <input
            id={ids.bis}
            type="date"
            className="feld__eingabe"
            value={bis}
            aria-invalid={zeitraumFalsch}
            onChange={(e) => setzeBis(e.target.value)}
          />
          <p className="feld__hilfe">
            {zeitraumFalsch ? 'Gültig ab liegt nach gültig bis.' : 'Leer: ohne Anfang oder ohne Ende.'}
          </p>
        </div>
        <div className="knopfreihe">
          <button type="button" className="knopf" onClick={beiSchliessen} disabled={laeuft}>
            Abbrechen
          </button>
          <button type="submit" className="knopf knopf--haupt" disabled={!vollstaendig || laeuft}>
            {wert === null ? 'Anlegen' : 'Speichern'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
